import { Metadata } from "@dropbox/dash-component-library";
import { Button, IconButton } from "@dropbox/dig-components/buttons";
import { LabelGroup } from "@dropbox/dig-components/label_group";
import { Skeleton } from "@dropbox/dig-components/skeleton";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Cluster, Stack } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  BriefcaseLine,
  CalendarLine,
  CelebrateLine,
  EmailLine,
  LocationLine,
  OutOfOfficeLine,
  SlackExternalLogo,
} from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashProfileCard } from "@mirage/analytics/events/types/click_dash_profile_card";
import { PAP_Shown_DashProfileCard } from "@mirage/analytics/events/types/shown_dash_profile_card";
import {
  UserProfileBackgroundImage,
  UserProfileCustomizedBackgroundImage,
} from "@mirage/mosaics/UserProfilePage/background/CustomizedBackground";
import { getProfileBackgroundForEmail } from "@mirage/mosaics/UserProfilePage/background/utils";
import { useUserProfileApi } from "@mirage/mosaics/UserProfilePage/hooks";
import { UserProfileAvatar } from "@mirage/mosaics/UserProfilePage/UserProfileAvatar";
import {
  UserProfileName,
  UserProfileRole,
} from "@mirage/mosaics/UserProfilePage/UserProfileInfo";
import {
  calculateTenure,
  convertPeopleRecommendationToBasicUserInfo,
  formatBirthday,
  formatUpcomingPTO,
  getUserProfilePageHref,
  isMoreThanToday,
  isValidEmail,
  toMetro,
} from "@mirage/mosaics/UserProfilePage/utils";
import type { PersonOrgChart } from "@mirage/service-people/types";
import { openURL } from "@mirage/service-platform-actions";
import i18n from "@mirage/translations";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { FeatureLine } from "@mirage/analytics/events/enums/feature_line";
import type {
  BasicUserInfo,
  UserInfo,
} from "@mirage/mosaics/UserProfilePage/types";

interface AnswersUserProfileCardProps {
  peopleRecommendation: PersonOrgChart;
  dashActionSurface: DashActionSurface | undefined;
  setPinnedAnswerLoadingState: (loadingState: PinnedAnswerLoadingState) => void;
}

export type PinnedAnswerLoadingState = "loading" | "loaded" | "none";

const ANSWERS_CARD_BACKGROUND_HEIGHT = "112px";

export const AnswersUserProfileCard = ({
  peopleRecommendation,
  dashActionSurface,
  setPinnedAnswerLoadingState,
}: AnswersUserProfileCardProps) => {
  const featureLine = "people";
  const user = useUserProfileApi({
    displayHandle: peopleRecommendation.displayHandle,
  });
  const { reportPapEvent, searchSessionManager } = useMirageAnalyticsContext();

  useEffect(() => {
    setPinnedAnswerLoadingState("loading");
  }, []);

  useEffect(() => {
    if (user?.account_id) {
      setPinnedAnswerLoadingState("loaded");
      reportPapEvent(
        PAP_Shown_DashProfileCard({
          dashActionSurface,
          featureLine,
          profileCardType: "serp_answers_card",
          searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
        }),
      );
    }
  }, [reportPapEvent, user?.account_id]);

  // Show skeleton while fetching user info
  if (user === undefined) {
    return (
      <Box style={{ height: "295px" }}>
        <Stack>
          <Skeleton.Text width={208} style={{ maxWidth: "373px" }} />
          <Skeleton.Text width={330} style={{ maxWidth: "592px" }} />
          <Skeleton.Text width={311} style={{ maxWidth: "558px" }} />
        </Stack>
      </Box>
    );
  }

  // Fallback to basic user info if user is not found
  if (user === null) {
    const basicUser =
      convertPeopleRecommendationToBasicUserInfo(peopleRecommendation);
    return (
      <BaseUserProfileCard
        user={basicUser}
        userProfileDetails={undefined} // Render buttons instead of details since basic user won't have profile details
        dashActionSurface={dashActionSurface}
        featureLine={featureLine}
      />
    );
  }

  return (
    <BaseUserProfileCard
      user={user}
      userProfileDetails={<AnswersUserProfileCardDetails user={user} />}
      dashActionSurface={dashActionSurface}
      featureLine={featureLine}
    />
  );
};

const BaseUserProfileCard = ({
  user,
  userProfileDetails,
  dashActionSurface,
  featureLine,
}: {
  user: UserInfo | BasicUserInfo;
  userProfileDetails?: React.ReactNode;
  dashActionSurface: DashActionSurface | undefined;
  featureLine: FeatureLine;
}) => {
  const { backgroundColor, backgroundTheme } = getProfileBackgroundForEmail(
    user.email,
  );
  const buttonsGroup = (
    <AnswersUserProfileCardButtonGroup
      user={user}
      dashActionSurface={dashActionSurface}
      featureLine={featureLine}
    />
  );
  return (
    <Stack width="100%">
      {"user_profile_settings" in user ? (
        <UserProfileCustomizedBackgroundImage
          user={user}
          height={ANSWERS_CARD_BACKGROUND_HEIGHT}
        />
      ) : (
        <UserProfileBackgroundImage
          color={backgroundColor}
          theme={backgroundTheme}
          height={ANSWERS_CARD_BACKGROUND_HEIGHT}
        />
      )}
      <Box
        style={{
          marginTop: "-100px",
          display: "flex",
          flexDirection: "column",
        }}
        paddingLeft="24"
        paddingBottom="16"
      >
        <UserProfileAvatar user={user} size="profile" />
        <Box
          display="flex"
          flexDirection="column"
          style={{ maxWidth: "736px" }}
        >
          <UserProfileName user={user} boxProps={{ marginTop: "4" }} />
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            marginBottom="10"
          >
            <Box>
              <UserProfileRole user={user} />
            </Box>
            {userProfileDetails ? buttonsGroup : null}
          </Box>
          {userProfileDetails ? (
            userProfileDetails
          ) : (
            <Box marginBottom="10">{buttonsGroup}</Box>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

const AnswersUserProfileCardButtonGroup = ({
  user,
  dashActionSurface,
  featureLine,
}: {
  user: Pick<UserInfo, "display_handle" | "email"> & {
    google_calendar_link?: string | null;
  };
  dashActionSurface: DashActionSurface | undefined;
  featureLine: FeatureLine;
}) => {
  const displayHandle = user.display_handle;
  const email = user.email;
  const googleCalendarLink = user.google_calendar_link;
  const navigate = useNavigate();
  const { reportPapEvent, searchSessionManager } = useMirageAnalyticsContext();

  const handleViewProfile = useCallback(() => {
    reportPapEvent(
      PAP_Click_DashProfileCard({
        dashActionSurface,
        featureLine,
        profileCardType: "serp_answers_card",
        searchSessionId: searchSessionManager.getSessionIdOrUndefined(),
      }),
    );

    const href = getUserProfilePageHref(displayHandle);
    if (href) {
      navigate(href, { state: { source: "serp_answers" } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayHandle]);

  return (
    <Box
      marginRight="24"
      style={{ minWidth: googleCalendarLink ? "186px" : "146px" }}
    >
      <Cluster gap="8">
        <Cluster.Item>
          <Button variant="primary" onClick={handleViewProfile}>
            {i18n.t("view_profile")}
          </Button>
        </Cluster.Item>
        {isValidEmail(email) && (
          <Cluster.Item>
            <IconButton
              variant="opacity"
              style={{ borderRadius: "var(--spacing__unit--1)" }}
              onClick={() => {
                reportPapEvent(
                  PAP_Click_DashProfileCard({
                    dashActionSurface,
                    featureLine,
                    searchSessionId:
                      searchSessionManager.getSessionIdOrUndefined(),
                  }),
                );

                openURL(`mailto:${email}`);
              }}
            >
              <UIIcon src={EmailLine} />
            </IconButton>
          </Cluster.Item>
        )}
        {!!googleCalendarLink?.length && (
          <Cluster.Item>
            <IconButton
              variant="opacity"
              style={{ borderRadius: "var(--spacing__unit--1)" }}
              onClick={() => {
                reportPapEvent(
                  PAP_Click_DashProfileCard({
                    dashActionSurface,
                    featureLine,
                    searchSessionId:
                      searchSessionManager.getSessionIdOrUndefined(),
                  }),
                );

                openURL(googleCalendarLink);
              }}
            >
              <UIIcon src={CalendarLine} />
            </IconButton>
          </Cluster.Item>
        )}
      </Cluster>
    </Box>
  );
};

const AnswersUserProfileCardDetails = ({
  user,
}: {
  user: Pick<
    UserInfo,
    | "display_handle"
    | "slack_message_link"
    | "city"
    | "location"
    | "upcoming_pto_list"
    | "birthday"
    | "tenure"
  >;
}) => {
  const iconSize = "small";
  const iconColor = "var(--dig-color__text__base)";
  const details: {
    icon: React.ReactNode;
    label: string;
    labelOnClick?: () => void;
    type: "slack" | "city" | "location" | "pto" | "birthday" | "tenure";
  }[] = [];

  if (user.slack_message_link) {
    details.push({
      icon: (
        <UIIcon size={iconSize} color={iconColor} src={SlackExternalLogo} />
      ),
      label: `@${user.display_handle}`,
      labelOnClick: () => {
        if (user.slack_message_link) {
          openURL(user.slack_message_link);
        }
      },
      type: "slack",
    });
  }

  if (user.city) {
    details.push({
      icon: <UIIcon size={iconSize} color={iconColor} src={LocationLine} />,
      label: user.city,
      type: "city",
    });
  } else if (user.location) {
    const metro = toMetro(user.location);
    if (metro) {
      details.push({
        icon: <UIIcon size={iconSize} color={iconColor} src={LocationLine} />,
        label: metro,
        type: "location",
      });
    }
  }

  const upcomingPTO = user.upcoming_pto_list?.[0];
  if (
    upcomingPTO?.start_date &&
    upcomingPTO?.end_date &&
    isMoreThanToday(upcomingPTO.start_date)
  ) {
    details.push({
      icon: <UIIcon size={iconSize} color={iconColor} src={OutOfOfficeLine} />,
      label: `${i18n.t("profile_pto")} ${formatUpcomingPTO(upcomingPTO)}`,
      type: "pto",
    });
  }

  if (user.birthday) {
    details.push({
      icon: <UIIcon size={iconSize} color={iconColor} src={CelebrateLine} />,
      label: formatBirthday(user.birthday, "short"),
      type: "birthday",
    });
  }

  if (user.tenure) {
    details.push({
      icon: <UIIcon size={iconSize} color={iconColor} src={BriefcaseLine} />,
      label: i18n.t("tenure_short", {
        formattedTenure: calculateTenure(user.tenure),
      }),
      type: "tenure",
    });
  }

  return (
    <Box>
      <Metadata size="medium" withDividers="bullet">
        {details.slice(0, 3).map((detail) => (
          <Metadata.Item
            key={`answers-user-profile-card-detail__${detail.type}`}
          >
            <LabelGroup
              accessoryGap="2"
              verticalAlignment="center"
              withStartAccessory={detail.icon}
              withLabel={
                <Text
                  color="faint"
                  size="small"
                  onClick={detail.labelOnClick}
                  style={
                    detail.labelOnClick ? { cursor: "pointer" } : undefined
                  }
                >
                  {detail.label}
                </Text>
              }
            />
          </Metadata.Item>
        ))}
      </Metadata>
    </Box>
  );
};
